import { useState, useEffect } from 'react';
import { Bell, X, Mail, MessageSquare, Calendar, Shield } from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import TabBar from './TabBar';
import VoiceButton from './VoiceButton';
import {
    getNotificationPermission,
    requestNotificationPermission,
    isNotificationSupported,
} from '../../services/notificationService';
import { verifyAdminAccess } from '../../services/adminService';

const NOTIFICATION_PROMPT_KEY = 'way2will_notification_prompt_shown';

// Get formatted date for header
function getFormattedDate(): string {
    const now = new Date();
    return now.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    });
}

// Notification type
type AppNotification = {
    id: string;
    type: 'email' | 'slack' | 'calendar' | 'task';
    title: string;
    from: string;
    time: string;
    read: boolean;
};

export default function AppLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [showNotifications, setShowNotifications] = useState(false);
    // Start with empty notifications - real ones will come from connected integrations
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [showPermissionModal, setShowPermissionModal] = useState(false);

    const unreadCount = notifications.filter((n) => !n.read).length;

    // Don't show voice button on certain screens like profile
    const showVoiceButton = !location.pathname.includes('/profile');

    // Auto-prompt for notification permission on first load
    useEffect(() => {
        if (!isNotificationSupported()) return;

        const alreadyPrompted = localStorage.getItem(NOTIFICATION_PROMPT_KEY);
        const currentPermission = getNotificationPermission();

        // Show modal if never prompted and permission not yet granted
        if (!alreadyPrompted && currentPermission === 'default') {
            // Small delay to let the app load first
            const timer = setTimeout(() => {
                setShowPermissionModal(true);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleEnableNotifications = async () => {
        localStorage.setItem(NOTIFICATION_PROMPT_KEY, 'true');
        const permission = await requestNotificationPermission();
        setShowPermissionModal(false);

        if (permission === 'denied') {
            // Could show a toast here, but permission is stored so won't re-prompt
        }
    };

    const handleDismissNotificationPrompt = () => {
        localStorage.setItem(NOTIFICATION_PROMPT_KEY, 'true');
        setShowPermissionModal(false);
    };

    const handleProfileClick = () => {
        navigate('/app/profile');
    };

    const handleMarkAllRead = () => {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    };

    const handleNotificationClick = (id: string) => {
        setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, read: true } : n))
        );
        setShowNotifications(false);
        // Navigate to inbox for now
        navigate('/app/inbox');
    };

    const getNotificationIcon = (type: 'email' | 'slack' | 'calendar' | 'task') => {
        switch (type) {
            case 'email':
                return <Mail className="w-4 h-4" />;
            case 'slack':
                return <MessageSquare className="w-4 h-4" />;
            case 'calendar':
                return <Calendar className="w-4 h-4" />;
            case 'task':
                return <Bell className="w-4 h-4" />;
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFB] dark:bg-[#0F1419] font-['-apple-system','BlinkMacSystemFont','Segoe_UI','Roboto','Helvetica_Neue','Arial',sans-serif] transition-colors duration-300">
            {/* Fixed Top Header */}
            <div className="fixed top-0 left-0 right-0 z-10 bg-white dark:bg-[#1A2832] shadow-[0_2px_8px_rgba(13,95,107,0.06),0_1px_2px_rgba(13,95,107,0.04)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.3)]">
                {/* Safe Area Top */}
                <div className="h-[env(safe-area-inset-top,0px)]"></div>
                {/* Header Bar */}
                <header className="h-14 flex items-center justify-between px-4">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                            <span className="text-base font-semibold text-[#1A2832] dark:text-white">
                                {getFormattedDate()}
                            </span>
                            {user && verifyAdminAccess(user.uid) && (
                                <button
                                    onClick={() => navigate('/app/admin')}
                                    className="p-1 hover:bg-[#F5F8FA] dark:hover:bg-[#243442] rounded-full text-[#0D5F6B] dark:text-[#5BC4A8]"
                                    title="Admin Portal"
                                >
                                    <Shield className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Notification Button */}
                        <div className="relative">
                            <button
                                onClick={() => setShowNotifications(!showNotifications)}
                                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-[#243442] active:bg-[#EEF3F6] dark:active:bg-[#2D3E4A]"
                            >
                                <Bell className="w-5 h-5 text-[#4A5C6A] dark:text-[#9EAAB4]" />
                            </button>
                            {unreadCount > 0 && (
                                <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#D66B5C] rounded-full flex items-center justify-center">
                                    <span className="text-xs font-semibold text-white">
                                        {unreadCount}
                                    </span>
                                </div>
                            )}
                        </div>
                        {/* Profile Avatar */}
                        <button
                            onClick={handleProfileClick}
                            className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-transparent hover:ring-[#0D5F6B]/20 transition"
                        >
                            <div
                                className="w-full h-full bg-[#D9EEF2] dark:bg-[#243442] flex items-center justify-center"
                            >
                                {user?.avatar ? (
                                    <img
                                        src={user.avatar}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-[#0D5F6B] dark:text-[#5BC4A8] font-bold text-sm">
                                        {user?.name?.charAt(0) || 'U'}
                                    </span>
                                )}
                            </div>
                        </button>
                    </div>
                </header>
            </div>

            {/* Notification Panel */}
            {showNotifications && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 z-20"
                        onClick={() => setShowNotifications(false)}
                    />
                    {/* Panel */}
                    <div className="fixed top-[calc(env(safe-area-inset-top,0px)+56px)] right-4 z-30 w-[320px] max-w-[calc(100vw-32px)] bg-white dark:bg-[#1A2832] rounded-2xl shadow-[0_12px_32px_rgba(13,95,107,0.12)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.4)] border border-[#E5EBF0] dark:border-[#2D3E4A]">
                        <div className="flex items-center justify-between p-4 border-b border-[#E5EBF0] dark:border-[#2D3E4A]">
                            <h3 className="text-base font-semibold text-[#1A2832] dark:text-white">Notifications</h3>
                            <div className="flex items-center gap-2">
                                {unreadCount > 0 && (
                                    <button
                                        onClick={handleMarkAllRead}
                                        className="text-sm text-[#0D5F6B] dark:text-[#5BC4A8] font-semibold"
                                    >
                                        Mark all read
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowNotifications(false)}
                                    className="w-6 h-6 flex items-center justify-center"
                                >
                                    <X className="w-4 h-4 text-[#7B8C9B] dark:text-[#9EAAB4]" />
                                </button>
                            </div>
                        </div>
                        <div className="max-h-[400px] overflow-auto">
                            {notifications.length === 0 ? (
                                <div className="p-8 text-center text-[#7B8C9B]">
                                    No notifications
                                </div>
                            ) : (
                                notifications.map((notification) => (
                                    <button
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification.id)}
                                        className={`w-full flex items-start gap-3 p-4 text-left hover:bg-[#F5F8FA] transition ${!notification.read ? 'bg-[#D9EEF2]/30' : ''
                                            }`}
                                    >
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${notification.type === 'email'
                                            ? 'bg-[#D9EEF2] text-[#0D5F6B]'
                                            : notification.type === 'slack'
                                                ? 'bg-[#EDE9F7] text-[#7B61C4]'
                                                : 'bg-[#D5F2EA] text-[#3DA58A]'
                                            }`}>
                                            {getNotificationIcon(notification.type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`text-sm ${!notification.read ? 'font-semibold text-[#1A2832]' : 'text-[#4A5C6A]'}`}>
                                                    {notification.title}
                                                </span>
                                                {!notification.read && (
                                                    <div className="w-2 h-2 rounded-full bg-[#0D5F6B]" />
                                                )}
                                            </div>
                                            <p className="text-xs text-[#7B8C9B]">
                                                {notification.from} • {notification.time}
                                            </p>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                        <button
                            onClick={() => {
                                setShowNotifications(false);
                                navigate('/app/inbox');
                            }}
                            className="w-full p-3 text-center text-sm font-semibold text-[#0D5F6B] border-t border-[#E5EBF0]"
                        >
                            View All in Inbox
                        </button>
                    </div>
                </>
            )}

            {/* Top Spacer */}
            <div>
                <div className="h-[env(safe-area-inset-top,0px)]"></div>
                <div className="h-14"></div>
            </div>

            {/* Main Content Area */}
            <main className="pb-4">
                <Outlet />
            </main>

            {/* Bottom Spacer */}
            <div>
                <div className="mt-4 h-[72px]"></div>
                <div className="h-[env(safe-area-inset-bottom,0px)]"></div>
                {showVoiceButton && <div className="h-14"></div>}
            </div>

            {/* Fixed Bottom Area */}
            <div className="fixed bottom-0 left-0 right-0 z-10 flex flex-col">
                {/* Voice FAB */}
                {showVoiceButton && (
                    <div className="flex flex-col items-end gap-4 px-4 pb-4">
                        <VoiceButton onClick={() => navigate('/app/voice')} />
                    </div>
                )}
                {/* Tab Bar */}
                <TabBar />
            </div>

            {/* Notification Permission Modal */}
            {showPermissionModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl">
                        <div className="w-14 h-14 bg-[#D9EEF2] rounded-full flex items-center justify-center mx-auto mb-4">
                            <Bell className="w-7 h-7 text-[#0D5F6B]" />
                        </div>
                        <h2 className="text-xl font-bold text-[#1A2832] text-center mb-2">
                            Stay on Track
                        </h2>
                        <p className="text-[#7B8C9B] text-center mb-6">
                            Enable notifications to get reminders for tasks and events so you never miss a deadline.
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={handleEnableNotifications}
                                className="w-full py-3 bg-[#0D5F6B] text-white rounded-xl font-semibold hover:bg-[#0A4E59] transition-colors"
                            >
                                Enable Notifications
                            </button>
                            <button
                                onClick={handleDismissNotificationPrompt}
                                className="w-full py-3 text-[#7B8C9B] font-medium hover:text-[#4A5C6A] transition-colors"
                            >
                                Maybe Later
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
